/**
 * conversationalRhythmEngine.js — FASE R6.
 *
 * Derives a rhythm profile from conversation state signals.
 * Does NOT inject text — returns a profile object that the coordinator
 * uses to decide which instructions to surface.
 *
 * Profile values:
 *   rhythmProfile: "compact" | "reflective" | "present" | "restrained" | "varied" | "functional"
 *   lengthBias:    "short" | "medium" | "normal" | "varied"
 *   sentenceVariety: "tight" | "varied" | "clear"
 *   density:       "sparse" | "moderate" | "full"
 */

const LOGISTIC_INTENTS = new Set(["planning", "logistics", "analysis", "events"]);
const HEAVY_EMOTIONS   = new Set(["grief", "urgent", "pastoral-care"]);

/**
 * @param {{
 *   messageCount:    number,
 *   emotionalWeight: string,
 *   sessionLoad:     string,
 *   intent:          string,
 * }} params
 * @returns {{
 *   rhythmProfile:   string,
 *   lengthBias:      string,
 *   sentenceVariety: string,
 *   density:         string,
 * }}
 */
export function buildRhythmProfile({ messageCount = 0, emotionalWeight = "neutral", sessionLoad = "low", intent = "general" }) {
  // Logistics and planning: natural, direct, no special rhythm shaping
  if (LOGISTIC_INTENTS.has(intent)) {
    return { rhythmProfile: "functional", lengthBias: "normal", sentenceVariety: "clear", density: "full" };
  }

  const isLate       = messageCount >= 8;
  const isEarly      = messageCount <= 2;
  const isHeavy      = HEAVY_EMOTIONS.has(emotionalWeight);
  const isHighLoad   = sessionLoad === "high";

  // Deep conversation + heavy emotion → pull back, less is more
  if (isHeavy && isLate) {
    return { rhythmProfile: "restrained", lengthBias: "short", sentenceVariety: "tight", density: "sparse" };
  }

  // High session load → shorter, softer structure
  if (isHighLoad) {
    return { rhythmProfile: "compact", lengthBias: "short", sentenceVariety: "tight", density: "sparse" };
  }

  // Early in heavy conversation → present, warm, not instructional
  if (isHeavy && isEarly) {
    return { rhythmProfile: "present", lengthBias: "medium", sentenceVariety: "varied", density: "moderate" };
  }

  // Mid-conversation, no heavy signal → reflective, varied
  if (!isEarly && !isLate) {
    return { rhythmProfile: "reflective", lengthBias: "medium", sentenceVariety: "varied", density: "moderate" };
  }

  return { rhythmProfile: "varied", lengthBias: "varied", sentenceVariety: "varied", density: "moderate" };
}
